import { FormEventHandler, useEffect, useRef, useState } from "react";
import styles from "./auth.module.scss";
import { Button, CircularProgress, IconButton, InputAdornment, MenuItem, Select, TextField } from "@mui/material";
import Avatar from "@mui/material/Avatar";
import AvatarGroup from "@mui/material/AvatarGroup";
import {
  loginAction,
  forgetPasswordAction,
  setLoading,
  forgetResetPassword,
  loginSSOAction,
  signUpAction
} from "./loginSlice";
import { useAppDispatch, useAppSelector } from "../store";
import { useNavigate, useSearchParams } from "react-router-dom";
import { spendAbleLogo } from "src/components/nav/logo";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import SetFirstTimePassword from "src/components/setFirstTimePassword/SetFirstTimePassword";
import CognitoAuth from "src/components/cognitoAuth/CognitoAuth";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { countryCodeData } from "src/types/countryCode";
import { checkEmailAvailability, checkUserNameAvailability } from "src/network/graphql/userService";
import {
  userNameValidation,
  generateUsername,
  logUserInteraction,
  logScreenView,
  removeUserDetail,
  emailValidation
} from "src/utils/utils";
import { parsePhoneNumberFromString } from "libphonenumber-js";
import { immediateToast } from "izitoast-react";
import Swal from "sweetalert2";
import inactiveAlertImage from "src/assets/images/InactiveAlert.png";
import { EMPTY_STRING } from "src/components/constant/constant";
import { RESET_EMAIL, RESET_PASSPORT } from "src/components/constant/queryConstants";
import { getConfig } from "src/config/Config";

function Login() {
  const [rightPanelActive, setRightPanelActive] = useState<boolean>(false);
  const [email, setEmail] = useState<string>(EMPTY_STRING);
  const [password, setPassword] = useState<string>(EMPTY_STRING);
  const [signUpEmail, setSignUpEmail] = useState<string>(EMPTY_STRING);
  const [signUpPassword, setSignUpPassword] = useState<string>(EMPTY_STRING);
  const [signUpConfirmPassword, setSignUpConfirmPassword] = useState<string>(EMPTY_STRING);
  const [firstName, setFirstName] = useState<string>(EMPTY_STRING);
  const [lastName, setLastName] = useState<string>(EMPTY_STRING);
  const [username, setUsername] = useState<string>(EMPTY_STRING);
  const [agreeToTerms, setAgreeToTerms] = useState<boolean>(false);
  const [contact, setContact] = useState<string>(EMPTY_STRING);
  const [phoneCountryCode, setPhoneCountryCode] = useState<string>("+61");
  const [forgotPassword, setForgotPassword] = useState<boolean>(false);
  const [registeredEmail, setRegisteredEmail] = useState<string>(EMPTY_STRING);
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const { loading, forgetPasswordLoading } = useAppSelector((state) => state.auth);
  const { showSetPassword } = useAppSelector((state) => state.user);
  const [emailError, setEmailError] = useState({
    email: EMPTY_STRING,
    loginemail: EMPTY_STRING,
    loginPassword: EMPTY_STRING,
    signUpfname: EMPTY_STRING,
    signUplname: EMPTY_STRING,
    signUpcontact: EMPTY_STRING,
    signUpemail: EMPTY_STRING,
    signUpPassword: EMPTY_STRING,
    signUpConfirmPassword: EMPTY_STRING,
    signUpUsername: EMPTY_STRING,
    verifCode: EMPTY_STRING,
    newResetPassword: EMPTY_STRING,
    tnc: EMPTY_STRING
  });
  const [resetPassword, setResetPassword] = useState<boolean>(false);
  const [verificationCode, setVerificationCode] = useState<string>(EMPTY_STRING);
  const [newResetPassword, setNewResetPassword] = useState<string>(EMPTY_STRING);
  const [showSpendableLogin, setShowSpendableLogin] = useState<boolean>(false);
  const [signUpLoading, setSignUpLoading] = useState<boolean>(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const code = searchParams.get("code");
  const [showSignUpPassword, setshowSignUpPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [lastActivity, setLastActivity] = useState<number>(Date.now());
  const inactivityTimer = useRef<NodeJS.Timeout | null>(null);

  const showSSO = getConfig().SHOW_SSO_LOGIN;

  useEffect(() => {
    if (!showSSO) {
      setShowSpendableLogin(true);
    }
  }, []);

  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (searchParams.get("signUp")) {
      setRightPanelActive(true);
    } else {
      setRightPanelActive(false);
    }
  }, [searchParams]);

  useEffect(() => {
    removeUserDetail();
  }, []);

  const hasResetPasswordRun = useRef(false);

  useEffect(() => {
    const resetPassport = searchParams.get(RESET_PASSPORT);
    const resetEmail = searchParams.get(RESET_EMAIL);

    if (resetPassport && resetEmail && emailValidation(resetEmail) && !hasResetPasswordRun.current) {
      hasResetPasswordRun.current = true;
      setForgotPassword(true);
      dispatch(forgetPasswordAction(resetEmail));
      setRegisteredEmail(resetEmail);
      setResetPassword(true);
    } else if (resetPassport) {
      setForgotPassword(true);
      hasResetPasswordRun.current = false;
    } else if (!resetPassport) {
      setForgotPassword(false);
      hasResetPasswordRun.current = false;
    }
  }, [searchParams, dispatch]);

  const handleSignUpClick = () => {
    // window.open("https://spendable.com.au/freetrial", "_blank");
    logScreenView("sign_up_visited");
    setSearchParams({ signUp: "true" });
  };

  const handleForgotPasswordClick = () => {
    searchParams.set(RESET_PASSPORT, "true");
    setSearchParams(searchParams);
  };

  const handleLoginClick = () => {
    searchParams.delete(RESET_PASSPORT);
    searchParams.delete(RESET_EMAIL);
    hasResetPasswordRun.current = false;
    setSearchParams(searchParams);
    setResetPassword(false);
    setRegisteredEmail("");
  };

  const handleSignInClick = () => {
    searchParams.delete("signUp");
    setSearchParams(searchParams);
  };

  const handleSignUp: FormEventHandler<HTMLFormElement> = async (event) => {
    event.preventDefault();

    const usernameError = userNameValidation(username);
    if (usernameError) {
      setEmailError({
        ...emailError,
        signUpUsername: usernameError
      });
      return;
    }

    if (
      !firstName.trim() ||
      !lastName.trim() ||
      !contact.trim() ||
      !signUpEmail.trim() ||
      !signUpPassword.trim() ||
      !signUpConfirmPassword.trim() ||
      !username.trim() ||
      !agreeToTerms
    ) {
      setEmailError({
        ...emailError,
        signUpfname: firstName.trim() ? EMPTY_STRING : "First Name is required",
        signUplname: lastName.trim() ? EMPTY_STRING : "Last Name is required",
        signUpcontact: contact.trim()
          ? parsePhoneNumberFromString(phoneCountryCode + contact)?.isValid()
            ? EMPTY_STRING
            : "Invalid contact number"
          : "Contact number is required",
        signUpemail: signUpEmail.trim() ? EMPTY_STRING : "Email is required",
        signUpPassword: signUpPassword.trim() ? EMPTY_STRING : "Password is required",
        signUpConfirmPassword: signUpConfirmPassword.trim()
          ? signUpConfirmPassword !== signUpPassword
            ? "Passwords do not match"
            : EMPTY_STRING
          : "Confirm Password is required",
        signUpUsername: username.trim() ? userNameValidation(username) : "Username is required",
        tnc: agreeToTerms ? EMPTY_STRING : "You must agree to the terms and conditions"
      });
      return;
    }

    setSignUpLoading(true);
    immediateToast("info", {
      message: "Hold tight, we are setting up your account",
      timeout: 3000,
      position: "topCenter"
    });

    const isUserNameAvailable = await checkUserNameAvailability(username);

    if (!isUserNameAvailable.data.is_username_available) {
      setSignUpLoading(false);
      setEmailError({
        ...emailError,
        signUpUsername: "Username already exists"
      });
      return;
    }

    const isEmailAvailable = await checkEmailAvailability(signUpEmail.toLowerCase());

    if (!isEmailAvailable.data.is_email_available) {
      setSignUpLoading(false);
      setEmailError({
        ...emailError,
        signUpemail: "Email already exists"
      });
      return;
    }

    const formattedFirstName = firstName.trim();
    const formattedLastName = lastName.trim();

    const data = {
      userName: username,
      email: signUpEmail.toLowerCase(),
      password: signUpPassword,
      firstName: formattedFirstName,
      lastName: formattedLastName,
      contact: String(phoneCountryCode + contact)
    };

    const loginDetail = {
      email: signUpEmail.toLowerCase(),
      password: signUpPassword
    };

    dispatch(
      signUpAction(data, async () => {
        logUserInteraction("signed_up", { name: data.firstName + " " + data.lastName, contact: data.contact });
        setSignUpLoading(false);
        searchParams.delete("signUp");
        setSearchParams(searchParams);
        await dispatch(
          loginAction(
            loginDetail,
            () => {
              navigate("/");
            },
            () => {
              navigate("/on-boarding");
            }
          )
        );
        setSignUpEmail(EMPTY_STRING);
        setSignUpPassword(EMPTY_STRING);
        setSignUpConfirmPassword(EMPTY_STRING);
        setAgreeToTerms(false);
        setFirstName(EMPTY_STRING);
        setLastName(EMPTY_STRING);
        setContact(EMPTY_STRING);
        setUsername(EMPTY_STRING);
        setEmailError({
          ...emailError,
          signUpfname: EMPTY_STRING,
          signUplname: EMPTY_STRING,
          signUpcontact: EMPTY_STRING,
          signUpemail: EMPTY_STRING,
          signUpPassword: EMPTY_STRING,
          signUpConfirmPassword: EMPTY_STRING,
          signUpUsername: EMPTY_STRING,
          tnc: EMPTY_STRING
        });
      })
    );
  };

  const handleSignIn: FormEventHandler<HTMLFormElement> = async (event) => {
    event.preventDefault();

    if (!email.trim() || !password.trim()) {
      setEmailError({
        ...emailError,
        loginemail: email.trim() ? EMPTY_STRING : "Email is required",
        loginPassword: password.trim() ? EMPTY_STRING : "Password is required"
      });
      return;
    }

    dispatch(
      loginAction(
        { email, password },
        () => {
          navigate("/");
        },
        () => {
          navigate("/on-boarding");
        }
      )
    );
  };

  const handleForgotPassword = () => {
    if (resetPassword) {
      if (!verificationCode.trim() || !newResetPassword.trim()) {
        setEmailError({
          ...emailError,
          verifCode: verificationCode ? EMPTY_STRING : "Verification Code is required",
          newResetPassword: newResetPassword ? EMPTY_STRING : "password is required"
        });
        return;
      }

      dispatch(
        forgetResetPassword(registeredEmail.toLowerCase(), newResetPassword, verificationCode, () => {
          setResetPassword(false);
          searchParams.delete(RESET_PASSPORT);
          setSearchParams(searchParams);
          setRegisteredEmail(EMPTY_STRING);
          setVerificationCode(EMPTY_STRING);
          setNewResetPassword(EMPTY_STRING);
        })
      );
    } else {
      if (!registeredEmail.trim()) {
        setEmailError({ ...emailError, email: "Email is required" });
        return;
      }

      if (!emailValidation(registeredEmail.toLowerCase())) {
        setEmailError({ ...emailError, email: "Invalid email format" });
        return;
      }

      hasResetPasswordRun.current = true;

      dispatch(forgetPasswordAction(registeredEmail.toLowerCase())).then(() => {
        searchParams.set(RESET_EMAIL, registeredEmail);
        searchParams.set(RESET_PASSPORT, "true");
        setSearchParams(searchParams);
        setResetPassword(true);
      });
    }
  };

  useEffect(() => {
    dispatch(setLoading(false));
  }, []);

  useEffect(() => {
    if (code) {
      dispatch(
        loginSSOAction(code, () => {
          console.log("logging in with SSO");
          navigate("/");
        })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  const handleContactBlur = () => {
    const phoneNumber = parsePhoneNumberFromString(phoneCountryCode + contact);

    if (!phoneNumber || !phoneNumber.isValid()) {
      setEmailError({
        ...emailError,
        signUpcontact: "Invalid contact number"
      });
    } else {
      setEmailError({
        ...emailError,
        signUpcontact: EMPTY_STRING
      });
    }
  };

  const generateAndSetUsername = async () => {
    if (firstName && lastName) {
      const formattedFirstName = firstName.trim().replace(/\s+/g, EMPTY_STRING);
      const formattedLastName = lastName.trim().replace(/\s+/g, EMPTY_STRING);

      const userName = await generateUsername(formattedFirstName, formattedLastName);

      if (userName) {
        setUsername(userName);
      } else {
        setUsername(EMPTY_STRING);
      }
    }
  };

  const showInactivityAlert = () => {
    logScreenView("user_inactive_in_signUp");
    Swal.fire({
      title:
        "<div style=display:flex;flex-direction:column;gap:15px;><span style=font-size:18px;>Want to speak to our team before you get started?</span><span style=font-size:18px;>Call <span style=font-size:21px;font-weight:700;>1800 519 530</span></span></div>",

      imageUrl: `${inactiveAlertImage}`,
      color: "#1e293b",
      showCancelButton: false,
      showConfirmButton: false,
      showCloseButton: true
    });
  };

  const handleActivity = () => {
    setLastActivity(Date.now());
  };

  useEffect(() => {
    if (!rightPanelActive) return;

    if (inactivityTimer.current) {
      clearTimeout(inactivityTimer.current);
    }

    inactivityTimer.current = setTimeout(() => {
      showInactivityAlert();
    }, 30000);

    return () => {
      if (inactivityTimer.current) {
        clearTimeout(inactivityTimer.current);
      }
    };
  }, [rightPanelActive, lastActivity]);

  const monitorActivity = (callback: (...args: any[]) => void) => {
    return (...args: any[]) => {
      handleActivity();
      callback(...args);
    };
  };

  const enhancedSetFirstName = monitorActivity((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    setFirstName(value);
  });

  const enhancedSetLastName = monitorActivity((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    setLastName(value);
  });

  const enhancedSetContact = monitorActivity((e: React.ChangeEvent<HTMLInputElement>) => {
    setContact(e.target.value);
  });

  const enhancedSetSignUpEmail = monitorActivity((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.trim().toLowerCase();

    setSignUpEmail(value);
  });

  const enhancedSetUsername = monitorActivity((e: React.ChangeEvent<HTMLInputElement>) => {
    setUsername(e.target.value.toLowerCase());
  });

  const enhancedSetSignUpPassword = monitorActivity((e: React.ChangeEvent<HTMLInputElement>) => {
    setSignUpPassword(e.target.value);
  });

  const enhancedSetSignUpConfirmPassword = monitorActivity((e: React.ChangeEvent<HTMLInputElement>) => {
    setSignUpConfirmPassword(e.target.value);
  });

  const enhancedSetAgreeToTerms = monitorActivity((e: React.ChangeEvent<HTMLInputElement>) => {
    setAgreeToTerms(e.target.checked);
  });

  return (
    <div className={`${styles.container} ${rightPanelActive ? styles.rightPanelActive : EMPTY_STRING}`} id="container">
      <div className={`${styles.formContainer} ${styles.signUpContainer}`}>
        <form onSubmit={handleSignUp}>
          <div className={styles.formIn}>
            {loading ? (
              <CircularProgress sx={{ color: "#0f172a" }} />
            ) : (
              <>
                <div className={styles.logo}>
                  <div className={styles.logoL}>{spendAbleLogo}</div>
                  <div className={styles.logoR}>
                    <h1 className={styles.title}>SpendAble</h1>
                    <div className={styles.subTitle}>Enabling Choices, Empowering Change</div>
                  </div>
                </div>
                <div className={styles.header}>
                  <h1>Step 1 - Create your admin account</h1>
                  <p>
                    <strong>For participants</strong> - usually a parent or financial guardian
                  </p>
                  <p>
                    <strong>For providers</strong> - usually a finance, service delivery or executive team member
                  </p>
                  {/* <p>If it&apos;s your first time here, you&apos;re in the right place</p> */}
                </div>
                <div className={styles.formInput}>
                  <div className={styles.name}>
                    <div className={styles.firstName}>
                      <label htmlFor="firstName">First Name</label>
                      <input
                        type="text"
                        id="firstName"
                        placeholder="first name"
                        value={firstName}
                        onChange={enhancedSetFirstName}
                        onBlur={() => generateAndSetUsername()}
                      />
                      {emailError.signUpfname && (
                        <div className={styles.error}>
                          <ErrorOutlineIcon className={styles.errIcon} />
                          {emailError.signUpfname}
                        </div>
                      )}
                    </div>
                    <div className={styles.lastName}>
                      <label htmlFor="lastName">Last Name</label>
                      <input
                        type="text"
                        id="lastName"
                        placeholder="last name"
                        value={lastName}
                        onChange={enhancedSetLastName}
                        onBlur={() => generateAndSetUsername()}
                      />
                      {emailError.signUplname && (
                        <div className={styles.error}>
                          <ErrorOutlineIcon className={styles.errIcon} />
                          {emailError.signUplname}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className={styles.contact}>
                    <label htmlFor="contact">Phone Number</label>
                    <div className={styles.contactCode}>
                      <Select
                        value={phoneCountryCode || "+61"}
                        onChange={(e) => setPhoneCountryCode(e.target.value)}
                        displayEmpty
                        name="phoneCountryCode"
                        className={styles.countryCodeSelect}
                        sx={{ height: "40px" }}
                        MenuProps={{ sx: { maxHeight: 250 } }}
                        disabled
                      >
                        {countryCodeData.map((country) => (
                          <MenuItem key={country.code} value={country.code}>
                            {country.code}
                          </MenuItem>
                        ))}
                      </Select>
                      <input
                        type="number"
                        id="contact"
                        placeholder="e.g. 0412345678"
                        value={contact}
                        onChange={enhancedSetContact}
                        onBlur={handleContactBlur}
                      />
                    </div>
                    {emailError.signUpcontact && (
                      <div className={styles.error}>
                        <ErrorOutlineIcon className={styles.errIcon} />
                        {emailError.signUpcontact}
                      </div>
                    )}
                  </div>
                  <div className={styles.email}>
                    <label htmlFor="email">Email Address</label>
                    <input
                      type="text"
                      id="email"
                      placeholder="example@gmail.com"
                      value={signUpEmail}
                      onChange={enhancedSetSignUpEmail}
                    />
                    {emailError.signUpemail && (
                      <div className={styles.error}>
                        <ErrorOutlineIcon className={styles.errIcon} />
                        {emailError.signUpemail}
                      </div>
                    )}
                  </div>
                  <div className={styles.username}>
                    <label htmlFor="username">Username</label>
                    <input
                      type="text"
                      id="username"
                      placeholder="i.e. (e.g. John.smith)"
                      value={username}
                      onChange={enhancedSetUsername}
                    />
                    {emailError.signUpUsername && (
                      <div className={styles.error}>
                        <ErrorOutlineIcon className={styles.errIcon} />
                        {emailError.signUpUsername}
                      </div>
                    )}
                  </div>

                  <div className={styles.password}>
                    <label htmlFor="pass">Password</label>
                    <TextField
                      id="pass"
                      type={showSignUpPassword ? "text" : "password"}
                      placeholder="Create a password"
                      value={signUpPassword}
                      onChange={enhancedSetSignUpPassword}
                      fullWidth
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setshowSignUpPassword(!showSignUpPassword)}
                              aria-label="toggle password visibility"
                            >
                              {showSignUpPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                            </IconButton>
                          </InputAdornment>
                        )
                      }}
                    />
                    {emailError.signUpPassword && (
                      <div className={styles.error}>
                        <ErrorOutlineIcon className={styles.errIcon} />
                        {emailError.signUpPassword}
                      </div>
                    )}
                  </div>

                  <div className={styles.password}>
                    <label htmlFor="confirmpass">Confirm Password</label>
                    <TextField
                      id="confirmpass"
                      type={showConfirmPassword ? "text" : "password"}
                      placeholder="Re-enter password"
                      value={signUpConfirmPassword}
                      onChange={enhancedSetSignUpConfirmPassword}
                      fullWidth
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                              aria-label="toggle confirm password visibility"
                            >
                              {showConfirmPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                            </IconButton>
                          </InputAdornment>
                        )
                      }}
                    />
                    {emailError.signUpConfirmPassword && (
                      <div className={styles.error}>
                        <ErrorOutlineIcon className={styles.errIcon} />
                        {emailError.signUpConfirmPassword}
                      </div>
                    )}
                  </div>
                  <div className={styles.checkboxContainer}>
                    <input
                      type="checkbox"
                      checked={agreeToTerms}
                      onChange={enhancedSetAgreeToTerms}
                      aria-label="term and condition"
                    />
                    <span>
                      I agree to the
                      <b
                        className={styles.tnc}
                        onClick={() => {
                          window.open(
                            "https://storage.googleapis.com/msgsndr/Pkn7yggrsCLAHtfI3qCv/media/666a85aeb1b532560e770720.pdf",
                            "_blank"
                          );
                        }}
                      >
                        Terms and conditions
                      </b>
                    </span>
                  </div>
                  {emailError.tnc && (
                    <div className={styles.error}>
                      <ErrorOutlineIcon className={styles.errIcon} />
                      {emailError.tnc}
                    </div>
                  )}
                </div>
                <div className={styles.signUp}>
                  <Button
                    type="submit"
                    variant="contained"
                    aria-label="Sign Up"
                    className={styles.SignUpBtn}
                    disabled={loading || signUpLoading}
                  >
                    {signUpLoading ? <CircularProgress sx={{ color: "white" }} /> : "Sign Up"}
                  </Button>
                </div>
                <div className={styles.overlayContainerL}>
                  <div className={styles.overlayL}>
                    <div className={`${styles.overlayPanelL} ${styles.overlayLeft}`}>
                      <span>
                        Already have an account?
                        <b className={styles.ghost} id="signIn" onClick={handleSignInClick}>
                          . Sign in
                        </b>
                      </span>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </form>
      </div>
      {rightPanelActive ? null : (
        <div className={`${styles.formContainer} ${styles.signInContainer}`}>
          <form onSubmit={handleSignIn}>
            <div className={styles.formIn}>
              {loading ? (
                <CircularProgress sx={{ color: "#0f172a" }} />
              ) : (
                <>
                  <div className={styles.logo}>
                    <div className={styles.logoL}>{spendAbleLogo}</div>
                    <div className={styles.logoR}>
                      <h1 className={styles.title}>SpendAble</h1>
                      <div className={styles.subTitle}>Enabling Choices, Empowering Change</div>
                    </div>
                  </div>
                  {showSetPassword && <SetFirstTimePassword handleLoginClick={handleLoginClick} />}
                  {forgotPassword && !showSetPassword ? (
                    <>
                      <div className={styles.header}>
                        <h1>Create new password</h1>
                      </div>
                      {forgetPasswordLoading ? (
                        <CircularProgress sx={{ color: "#0f172a" }} />
                      ) : (
                        <>
                          <div className={styles.formInput}>
                            <div className={styles.email}>
                              <label htmlFor="RegistEmail">Type your email here</label>
                              <TextField
                                type="email"
                                id="RegistEmail"
                                placeholder="EMAIL"
                                value={registeredEmail}
                                disabled={resetPassword}
                                onChange={(e) => {
                                  setRegisteredEmail(e.target.value.toLowerCase());
                                  if (!e.target.value) {
                                    setEmailError({ ...emailError, email: "Email is required" });
                                  } else {
                                    setEmailError({ ...emailError, email: EMPTY_STRING });
                                  }
                                }}
                                sx={{
                                  width: "100%",
                                  borderRadius: "6px",
                                  borderColor: "#ced4da",
                                  input: {
                                    backgroundColor: resetPassword ? "#90909024 !important" : EMPTY_STRING
                                  }
                                }}
                              />
                              {resetPassword && (
                                <div
                                  className={styles.editEmail}
                                  onClick={() => {
                                    setResetPassword(false);
                                  }}
                                >
                                  Edit Email
                                </div>
                              )}
                              {emailError.email && (
                                <div className={styles.error}>
                                  <ErrorOutlineIcon className={styles.errIcon} />
                                  {emailError.email}
                                </div>
                              )}
                            </div>
                            {resetPassword && (
                              <>
                                <p className={styles.info}>
                                  * A verification code has been sent to your email. Check your inbox (or spam folder)
                                  and enter the code below. Then, set your new password to complete the process.
                                </p>
                                <div className={styles.email}>
                                  <label htmlFor="verificationCode">Verify Code</label>
                                  <TextField
                                    type="text"
                                    id="verificationCode"
                                    placeholder="Verification Code"
                                    value={verificationCode}
                                    onChange={(e) => {
                                      setVerificationCode(e.target.value);
                                      if (!e.target.value) {
                                        setEmailError({ ...emailError, verifCode: "Verification Code is required" });
                                      } else {
                                        setEmailError({ ...emailError, verifCode: EMPTY_STRING });
                                      }
                                    }}
                                    sx={{ width: "100%", borderRadius: "6px", borderColor: "#ced4da" }}
                                  />
                                  {emailError.verifCode && (
                                    <div className={styles.error}>
                                      <ErrorOutlineIcon className={styles.errIcon} />
                                      {emailError.verifCode}
                                    </div>
                                  )}
                                </div>
                                <div className={styles.email}>
                                  <label htmlFor="newResetPassword">New Password</label>
                                  <TextField
                                    type="text"
                                    id="newResetPassword"
                                    placeholder="Password"
                                    value={newResetPassword}
                                    onChange={(e) => {
                                      setNewResetPassword(e.target.value);
                                      if (!e.target.value) {
                                        setEmailError({ ...emailError, newResetPassword: "Password is required" });
                                      } else {
                                        setEmailError({ ...emailError, newResetPassword: EMPTY_STRING });
                                      }
                                    }}
                                    sx={{ width: "100%", borderRadius: "6px", borderColor: "#ced4da" }}
                                  />
                                  {emailError.newResetPassword && (
                                    <div className={styles.error}>
                                      <ErrorOutlineIcon className={styles.errIcon} />
                                      {emailError.newResetPassword}
                                    </div>
                                  )}
                                </div>
                              </>
                            )}
                          </div>
                          <div className={styles.signIn}>
                            <Button
                              variant="contained"
                              aria-label="Reset Password"
                              className={styles.SignInBtn}
                              disabled={forgetPasswordLoading}
                              onClick={handleForgotPassword}
                            >
                              {resetPassword ? "Confirm" : "Send"}
                            </Button>
                          </div>
                          <div className={styles.overlayContainerR}>
                            <div className={styles.overlayR}>
                              <div className={`${styles.overlayPanelR} ${styles.overlayRight}`}>
                                <span>
                                  Go Back To
                                  <b onClick={handleLoginClick}>. Login</b>
                                </span>
                              </div>
                            </div>
                          </div>
                        </>
                      )}
                    </>
                  ) : (
                    !showSetPassword && (
                      <>
                        <div className={styles.header}>
                          <h1>Welcome back</h1>
                        </div>
                        {showSpendableLogin ? (
                          <>
                            <div className={styles.formInput}>
                              <div className={styles.email}>
                                <label htmlFor="emailAdd">Email Address</label>
                                <TextField
                                  type="email"
                                  id="emailAdd"
                                  placeholder="EMAIL"
                                  value={email}
                                  onChange={(e) => setEmail(e.target.value.toLowerCase())}
                                  sx={{ width: "100%", borderRadius: "6px", borderColor: "#ced4da" }}
                                />
                                {emailError.loginemail && (
                                  <div className={styles.error}>
                                    <ErrorOutlineIcon className={styles.errIcon} />
                                    {emailError.loginemail}
                                  </div>
                                )}
                              </div>
                              <div className={styles.password}>
                                <label htmlFor="passw">Password</label>
                                <TextField
                                  type={showPassword ? "text" : "password"}
                                  id="passw"
                                  placeholder="PASSWORD"
                                  value={password}
                                  onChange={(e) => setPassword(e.target.value)}
                                  InputProps={{
                                    endAdornment: (
                                      <InputAdornment position="end">
                                        <IconButton
                                          aria-label="toggle password visibility"
                                          onClick={() => setShowPassword(!showPassword)}
                                        >
                                          {showPassword ? <VisibilityIcon /> : <VisibilityOffIcon />}
                                        </IconButton>
                                      </InputAdornment>
                                    )
                                  }}
                                  sx={{ width: "100%", borderRadius: "6px", borderColor: "#ced4da" }}
                                />
                                {emailError.loginPassword && (
                                  <div className={styles.error}>
                                    <ErrorOutlineIcon className={styles.errIcon} />
                                    {emailError.loginPassword}
                                  </div>
                                )}
                              </div>
                              <div className={styles.checkboxContainer}>
                                <div className={styles.checkboxL}></div>
                                {!showSSO && (
                                  <div className={styles.checkboxR}>
                                    <a href="#" onClick={handleForgotPasswordClick}>
                                      Need help with your password?
                                    </a>
                                  </div>
                                )}
                              </div>
                            </div>
                            <div className={styles.signIn}>
                              <Button
                                type="submit"
                                variant="contained"
                                className={styles.SignInBtn}
                                disabled={loading}
                                aria-label="Sign In"
                              >
                                Sign In
                              </Button>
                            </div>
                            <div className={styles.overlayContainerR}>
                              <div className={styles.overlayR}>
                                <div className={`${styles.overlayPanelR} ${styles.overlayRight}`}>
                                  {!showSSO ? (
                                    <span>
                                      Don&apos;t have an account?
                                      <b className={styles.ghost} id="signUp" onClick={handleSignUpClick}>
                                        . Sign up
                                      </b>
                                    </span>
                                  ) : (
                                    <span>
                                      <ArrowBackIcon fontSize="small" sx={{ width: "15px" }} />
                                      <b
                                        className={styles.ghost}
                                        id="signUp"
                                        onClick={() => setShowSpendableLogin(false)}
                                      >
                                        Go back
                                      </b>
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </>
                        ) : (
                          <>
                            <CognitoAuth />
                            {/* <div className={styles.divider}>
                              <hr />
                              OR
                              <hr />
                            </div>
                            <Button
                              variant="contained"
                              className={styles.Spendablebtn}
                              disabled={loading}
                              onClick={() => setShowSpendableLogin(true)}
                            >
                              Login with Spendable
                            </Button> */}
                          </>
                        )}
                      </>
                    )
                  )}
                </>
              )}
            </div>
          </form>
        </div>
      )}
      <div className={styles.overlayContainer}>
        <div className={styles.overlay}>
          <div className={`${styles.overlayPanel} ${styles.overlayLeft}`}>
            <div className={styles.leftBg}>
              <div className={styles.leftCard}>
                <div className={styles.headText}>
                  <span>Support good </span>
                  <span>money handling</span>
                </div>
                <div className={styles.midText}>
                  Access the tools and accounts to safely handle money for older people and people with disability
                </div>
                <div className={styles.bottomText}>
                  <div className={styles.icon}>
                    <AvatarGroup className={styles.AvatarGroup}>
                      <Avatar
                        alt="Remy Sharp"
                        src={"https://randomuser.me/api/portraits/men/1.jpg"}
                        className={styles.avatar}
                      />
                      <Avatar
                        alt="Travis Howard"
                        src={"https://randomuser.me/api/portraits/women/2.jpg"}
                        className={styles.avatar}
                      />
                      <Avatar
                        alt="Cindy Baker"
                        src={"https://randomuser.me/api/portraits/men/3.jpg"}
                        className={styles.avatar}
                      />
                      <Avatar
                        alt="Agnes Walker"
                        src={"https://randomuser.me/api/portraits/women/4.jpg"}
                        className={styles.avatar}
                      />
                    </AvatarGroup>
                  </div>
                  <span>Join 1,000's of other people!</span>
                </div>
              </div>
            </div>
          </div>
          <div className={`${styles.overlayPanel} ${styles.overlayRight}`}>
            <div className={styles.rightBg}>
              <div className={styles.rightCard}>
                <div className={styles.topText}>You&apos;re part of the solution to</div>
                <div className={styles.bottomText}>financial abuse</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Login;
